import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "./filter-options-tenant.scss";
import { OnboardedDateFilterProps } from "./tenant-filter-helper";
import { useFormikContext } from "formik";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

const OnboardedDateFilter: React.FC<OnboardedDateFilterProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  startDateError,
  endDateError,
}) => {
  const { setFieldError } = useFormikContext<any>();
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null
  );
  const [toDate, setToDate] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null
  );
  const [errorStart, setErrorStart] = useState<string | null>(
    startDateError ?? null
  );
  const [errorEnd, setErrorEnd] = useState<string | null>(endDateError ?? null);

  useEffect(() => {
    setFromDate(startDate ? dayjs(startDate) : null);
  }, [startDate]);

  useEffect(() => {
    setToDate(endDate ? dayjs(endDate) : null);
  }, [endDate]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    const isInvalid =
      !newValue || !newValue.isValid() || newValue.year() < 1900;

    if (isInvalid) {
      setFromDate(null);
      setErrorStart("Invalid date");
      onDateRangeChange(null, toDate ? toDate.format("YYYY-MM-DD") : null);
      return;
    }

    if (toDate && newValue.isAfter(toDate)) return;

    setFromDate(newValue);
    const formattedStart = newValue.format("YYYY-MM-DD");
    const formattedEnd = toDate ? toDate.format("YYYY-MM-DD") : null;
    onDateRangeChange(formattedStart, formattedEnd);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    const isInvalid =
      !newValue ||
      !newValue.isValid() ||
      newValue.year() < 1900 ||
      newValue.isAfter(dayjs());

    if (isInvalid) {
      setToDate(null);
      setErrorEnd("Invalid date");
      onDateRangeChange(fromDate ? fromDate.format("YYYY-MM-DD") : null, null);
      return;
    }

    if (fromDate && newValue.isBefore(fromDate)) return;

    setToDate(newValue);
    const formattedStart = fromDate ? fromDate.format("YYYY-MM-DD") : null;
    const formattedEnd = newValue.format("YYYY-MM-DD");
    onDateRangeChange(formattedStart, formattedEnd);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="tenant-filter-date-input-wrapper">
        <DesktopDatePicker
          label="From Date"
          value={fromDate}
          onChange={handleStartDateChange}
          maxDate={toDate ?? dayjs()}
          minDate={dayjs("1900-01-01")}
          disableFuture
          shouldDisableDate={(date) =>
            !!(toDate && date.isAfter(toDate)) || date.isAfter(dayjs())
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: !!startDateError,
              helperText: startDateError,
              sx: {
                "& label.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  padding: "0",
                  margin: "0",
                },
              },
            },
          }}
        />
        <DesktopDatePicker
          label="To Date"
          value={toDate}
          onChange={handleEndDateChange}
          minDate={
            fromDate && fromDate.isBefore(dayjs("1900-01-01"))
              ? dayjs("1900-01-01")
              : (fromDate ?? undefined)
          }
          maxDate={dayjs()}
          disableFuture
          shouldDisableDate={(date) =>
            !!(fromDate && date.isBefore(fromDate)) || date.isAfter(dayjs())
          }
          onError={(error) => {
            if (error === "minDate") {
              setErrorEnd("Date cannot be before From Date");
            } else if (error === null) {
              setFieldError("onBoardedAtEnd", "");
            }
          }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: !!endDateError,
              helperText: endDateError,
              sx: {
                "& label.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  padding: "0",
                  margin: "0",
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default OnboardedDateFilter;
